/**
 * ConsoleView - AIエージェント向けコンソールモード
 *
 * シンプルなCUIを提供し、AIエージェントがMIDASを操作できるようにする。
 * DOM構造が最小限なので、コンテキスト消費を抑えられる。
 *
 * 特徴:
 * - x:プレフィックスなしでコマンド実行可能
 * - 結果はJSON形式で表示
 * - DOM表示は直近1件のみ（履歴は開発者ツールのコンソールで確認可能）
 * - 矢印キーでコマンド履歴を参照可能
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCommandExecutor } from '../hooks/useCommandExecutor';
import { useProject } from '../hooks/useProject';
import { commandRegistry } from '../command-system/CommandRegistry';
import { initializeCommandSystem } from '../command-system';
import type { CommandResult } from '../command-system/types';
import * as styles from '../styles/components/console-view.css';
import logger from '../utils/logger';

interface HistoryEntry {
  id: string;
  command: string;
  result: CommandResult;
  timestamp: Date;
}

/**
 * ConsoleView - コンソールモードのメインコンポーネント
 */
export function ConsoleView() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { execute } = useCommandExecutor();

  // プロジェクトデータをロード（OPFSから読み込み）
  const { loading: projectLoading, error: projectError } = useProject(projectId || '');

  const [inputValue, setInputValue] = useState('');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  // コマンドシステムの初期化
  useEffect(() => {
    initializeCommandSystem();
  }, []);

  // 出力エリアを自動スクロール
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [history]);

  // 入力にフォーカス
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // コマンド実行
  const executeCommand = useCallback(async (input: string) => {
    const trimmed = input.trim();
    if (!trimmed || isExecuting) return;

    setIsExecuting(true);
    setHistoryIndex(-1);

    // コンソールモードではx:プレフィックスなしでもOK
    // 自動的にx:を追加する
    const commandInput = trimmed.startsWith('x:') ? trimmed : `x:${trimmed}`;

    logger.info('[ConsoleView] Executing command', { input: trimmed, commandInput });

    try {
      const result = await execute(commandInput);

      // 結果をログに出力（開発者ツールで履歴を確認可能）
      logger.info('[ConsoleView] Command result', { command: trimmed, result });

      const entry: HistoryEntry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        command: trimmed,
        result,
        timestamp: new Date(),
      };

      setHistory(prev => [...prev, entry]);
      setInputValue('');
    } catch (error) {
      const errorResult: CommandResult = {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
        error: {
          code: 'EXECUTION_ERROR',
          message: error instanceof Error ? error.message : String(error),
        },
      };

      // エラー結果もログに出力
      logger.info('[ConsoleView] Command error', { command: trimmed, result: errorResult });

      const entry: HistoryEntry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        command: trimmed,
        result: errorResult,
        timestamp: new Date(),
      };

      setHistory(prev => [...prev, entry]);
      setInputValue('');
    } finally {
      setIsExecuting(false);
    }
  }, [execute, isExecuting]);

  // キーボードハンドラ
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        executeCommand(inputValue);
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (history.length > 0) {
          const newIndex = historyIndex < 0
            ? history.length - 1
            : Math.max(0, historyIndex - 1);
          setHistoryIndex(newIndex);
          setInputValue(history[newIndex].command);
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (historyIndex >= 0) {
          const newIndex = historyIndex + 1;
          if (newIndex >= history.length) {
            setHistoryIndex(-1);
            setInputValue('');
          } else {
            setHistoryIndex(newIndex);
            setInputValue(history[newIndex].command);
          }
        }
        break;

      case 'Escape':
        e.preventDefault();
        setInputValue('');
        setHistoryIndex(-1);
        break;
    }
  }, [inputValue, history, historyIndex, executeCommand]);

  // 通常モードに戻る
  const handleExit = useCallback(() => {
    if (projectId) {
      navigate(`/project/${projectId}`);
    } else {
      navigate('/');
    }
  }, [navigate, projectId]);

  // 履歴クリア
  const handleClear = useCallback(() => {
    setHistory([]);
  }, []);

  // 結果をフォーマット
  const formatResult = (result: CommandResult): string => {
    return JSON.stringify(result, null, 2);
  };

  // 利用可能なコマンド一覧
  const availableCommands = commandRegistry.getAll();

  // ローディング中の表示
  if (projectLoading) {
    return (
      <div className={styles.consoleContainer} data-testid="console-view">
        <div className={styles.consoleHeader}>
          <div className={styles.consoleTitle}>MIDAS Console</div>
        </div>
        <div className={styles.consoleOutput}>
          <div className={styles.consoleWelcome}>
            <div className={styles.consoleWelcomeTitle}>Loading project...</div>
          </div>
        </div>
      </div>
    );
  }

  // エラー時の表示
  if (projectError) {
    return (
      <div className={styles.consoleContainer} data-testid="console-view">
        <div className={styles.consoleHeader}>
          <div className={styles.consoleTitle}>MIDAS Console</div>
          <div className={styles.consoleActions}>
            <button className={styles.consoleExitButton} onClick={handleExit} type="button">
              Exit Console
            </button>
          </div>
        </div>
        <div className={styles.consoleOutput}>
          <div className={styles.consoleWelcome}>
            <div className={styles.consoleWelcomeTitle}>Error loading project</div>
            <div className={styles.consoleWelcomeText}>{projectError}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.consoleContainer} data-testid="console-view">
      {/* ヘッダー */}
      <div className={styles.consoleHeader}>
        <div className={styles.consoleTitle}>MIDAS Console</div>
        <div className={styles.consoleActions}>
          <button
            className={styles.consoleExitButton}
            onClick={handleClear}
            type="button"
          >
            Clear
          </button>
          <button
            className={styles.consoleExitButton}
            onClick={handleExit}
            type="button"
          >
            Exit Console
          </button>
        </div>
      </div>

      {/* 出力エリア */}
      <div className={styles.consoleOutput} ref={outputRef}>
        {/* ウェルカムメッセージ */}
        {history.length === 0 && (
          <div className={styles.consoleWelcome}>
            <div className={styles.consoleWelcomeTitle}>MIDAS Console Mode</div>
            <div className={styles.consoleWelcomeText}>
              This is an AI agent interface for MIDAS. Commands can be entered without the &quot;x:&quot; prefix.
            </div>
            <ul className={styles.consoleHelpList}>
              <li className={styles.consoleHelpItem}>
                <span className={styles.consoleHelpCommand}>status</span> - Get project status
              </li>
              <li className={styles.consoleHelpItem}>
                <span className={styles.consoleHelpCommand}>list {`{"resource":"datasets"}`}</span> - List datasets
              </li>
              <li className={styles.consoleHelpItem}>
                <span className={styles.consoleHelpCommand}>describe {`{"id":"dataset_1"}`}</span> - Get resource details
              </li>
              <li className={styles.consoleHelpItem}>
                <span className={styles.consoleHelpCommand}>tab {`{"type":"sql-editor","title":"Query"}`}</span> - Create a tab
              </li>
              {availableCommands.slice(0, 6).map(cmd => (
                <li key={cmd.name} className={styles.consoleHelpItem}>
                  <span className={styles.consoleHelpCommand}>{cmd.name}</span> - {cmd.description}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 直近1件の結果のみ表示（履歴は開発者ツールで確認可能） */}
        {history.slice(-1).map(entry => (
          <div key={entry.id} className={styles.consoleEntry}>
            <div className={styles.consoleCommand}>
              <span className={styles.consolePrompt}>&gt;</span>
              <span className={styles.consoleCommandText}>{entry.command}</span>
            </div>
            <pre
              className={`${styles.consoleResult} ${
                entry.result.success
                  ? styles.consoleResultSuccess
                  : styles.consoleResultError
              }`}
            >
              {formatResult(entry.result)}
            </pre>
          </div>
        ))}
      </div>

      {/* 入力エリア */}
      <div className={styles.consoleInputArea}>
        <span className={styles.consoleInputPrompt}>&gt;</span>
        <input
          ref={inputRef}
          type="text"
          className={`${styles.consoleInput} ${isExecuting ? styles.consoleInputDisabled : ''}`}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isExecuting ? 'Executing...' : 'Enter command (e.g., status, list {"resource":"datasets"})'}
          disabled={isExecuting}
          autoComplete="off"
          spellCheck={false}
          data-testid="console-input"
        />
      </div>
    </div>
  );
}
